import { useStore } from "vuex";

export const MAX_CSV_BYTES = 2 * 1024 * 1024;
export const MAX_CSV_ROWS = 500;
export const CSV_TEMPLATE_ROWS = ["sku,qty", "EXAMPLE-SKU-1,2", "EXAMPLE-SKU-2,10"];

function downloadCsv(text, filename) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8;" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function downloadCsvTemplate(filename = "medusa-order-template.csv") {
  downloadCsv(CSV_TEMPLATE_ROWS.join("\r\n"), filename);
}

export function downloadCsvRows(rows, filename = "medusa-unmatched-products.csv") {
  // User-supplied SKUs must stay literal when a downloaded CSV opens in Excel.
  const escape = value => {
    const text = String(value ?? "");
    const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  downloadCsv(["sku,qty", ...rows.map(row => `${escape(row.sku)},${escape(row.qty)}`)].join("\r\n"), filename);
}

export function validateCsvFile(file) {
  if (!file) return "Choose a CSV or Excel file.";
  if (!/\.(csv|xlsx)$/i.test(file.name)) return "Please choose a .csv or .xlsx file.";
  if (file.size > MAX_CSV_BYTES) return "That file is too large. Choose a file up to 2 MB.";
  if (!file.size) return "This file is empty. Fill in the template and choose it again.";
  return null;
}

// The inline Lightning results and the guided cart modal share these buckets.
export function emptySummary(overrides = {}) {
  return {
    successCount: 0, totalCount: 0, duplicateRowCount: 0,
    invalidQtySkus: [], combinedSkus: [], missingSkus: [], restrictedSkus: [],
    outOfStockSkus: [], duplicateSkus: [], cappedSkus: [], addedRows: [],
    missingRows: [], restrictedRows: [], outOfStockRows: [], duplicateRows: [],
    cappedRows: [], invalidRows: [], noPriceSkus: [], noPriceRows: [], ...overrides,
  };
}

const headerKey = value => String(value ?? "").trim().toLowerCase().replace(/[_-]/g, " ").replace(/\s+/g, " ");
const skuHeaders = ["sku", "product sku", "variant sku", "item sku"];
const qtyHeaders = ["qty", "quantity", "order quantity", "order qty"];
// Real SKUs never contain these. Rejecting them per row keeps one bad cell from
// failing the whole lookup (the API firewall blocks script/SQL-like payloads).
const INVALID_SKU_CHARS = /[<>=;`{}]|[\u0000-\u001f]/;

// Preserve quoted commas, escaped quotes, multiline cells and physical row numbers.
function readDelimitedText(text, delimiter) {
  const rows = [];
  let cells = [], cell = "", quoted = false, closedQuote = false, row = 1, startRow = 1;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '"') {
      if (quoted && text[i + 1] === '"') { cell += '"'; i++; }
      else if (quoted) { quoted = false; closedQuote = true; }
      else if (!cell.trim() && !closedQuote) { cell = ""; quoted = true; }
      else if (closedQuote) return { error: `Check the quotation marks on row ${startRow}.` };
      // A quote inside an unquoted cell is literal, e.g. an inch mark in GLASS 8" BONG.
      else cell += char;
    } else if (!quoted && (char === delimiter || char === "\n" || char === "\r")) {
      cells.push(cell);
      cell = "";
      closedQuote = false;
      if (char !== delimiter) {
        rows.push({ row: startRow, cells });
        cells = [];
        if (char === "\r" && text[i + 1] === "\n") i++;
        row++;
        startRow = row;
      }
    } else {
      if (closedQuote && char.trim()) return { error: `Check the separator after the quoted value on row ${startRow}.` };
      cell += char;
      if (char === "\n") row++;
    }
  }
  if (quoted) return { error: `A quoted value on row ${startRow} is not closed.` };
  cells.push(cell);
  rows.push({ row: startRow, cells });
  return { rows };
}

// A first row with 2+ filled cells and no digits at all is a header row (a data
// row always has a quantity). Files with no header row are still supported.
function looksLikeHeader(cells) {
  const filled = cells.map(cell => String(cell ?? "").trim()).filter(Boolean);
  return filled.length >= 2 && filled.every(cell => !/[0-9]/.test(cell));
}

export function parseOrderRows(inputRows) {
  const rows = inputRows.filter(row => row.cells.some(cell => String(cell ?? "").trim()));
  const qtyBySku = Object.create(null);
  const originalCase = Object.create(null);
  const entries = [];
  const invalidRows = [];
  const combinedSkus = [];
  const bySku = new Map();
  let skuIndex = 0, qtyIndex = 1, duplicateRowCount = 0;
  const headers = (rows[0]?.cells || []).map(headerKey);
  const headerSkuIndex = headers.findIndex(value => skuHeaders.includes(value));
  const headerQtyIndex = headers.findIndex(value => qtyHeaders.includes(value));
  const hasHeader = headerSkuIndex >= 0 && !headers.some((value, index) => index !== headerSkuIndex && /^[+-]?(?:\d*\.?\d+)$/.test(value));
  if (hasHeader && headerQtyIndex < 0 && headers.filter(Boolean).length > 1) {
    // e.g. "sku,price": guessing qty 1 for every row would order the wrong amounts.
    return { error: "The file needs a qty column. Use the template’s sku and qty headings.", entries, invalidRows };
  }
  if (hasHeader) {
    skuIndex = headerSkuIndex;
    qtyIndex = headerQtyIndex;
    rows.shift();
  } else if (headerQtyIndex >= 0) {
    return { error: "The file needs a SKU column. Use the template’s sku and qty headings.", entries, invalidRows };
  } else if (looksLikeHeader(rows[0]?.cells || [])) {
    // e.g. "name,amount": reading it by position would import the wrong columns.
    return { error: "We couldn’t find SKU and QTY columns. Use the template.", entries, invalidRows };
  }
  if (rows.length > MAX_CSV_ROWS) {
    return { error: `This list has more than ${MAX_CSV_ROWS} product rows. Import ${MAX_CSV_ROWS} or fewer at a time.`, entries, invalidRows };
  }
  rows.forEach(({ row, cells }) => {
    const sku = String(cells[skuIndex] ?? "").trim().replace(/^'(?=[=+\-@])/, "");
    const rawQty = qtyIndex < 0 ? "" : String(cells[qtyIndex] ?? "").trim();
    const qty = rawQty === "" ? 1 : Number(rawQty);
    const entry = { row, sku, qty, rawQty, defaulted: rawQty === "" };
    if (!sku) entry.reason = "SKU cell is empty";
    else if (sku.length > 255) entry.reason = "SKU is too long (maximum 255 characters)";
    else if (INVALID_SKU_CHARS.test(sku)) entry.reason = "SKU contains characters SKUs never use (< > = ; etc.)";
    else if (!hasHeader && cells.length > 2) entry.reason = "Use one SKU and one quantity per line";
    else if (!Number.isSafeInteger(qty) || qty < 1 || !/^\d+$/.test(rawQty || "1")) entry.reason = "Quantity must be a whole number of 1 or more";
    if (entry.reason) {
      invalidRows.push({ ...entry, qty: rawQty, type: "invalid" });
      return;
    }
    const key = sku.toUpperCase();
    if (bySku.has(key)) {
      const previous = bySku.get(key);
      if (!combinedSkus.includes(previous.sku)) combinedSkus.push(previous.sku);
      previous.qty += qty;
      previous.defaulted = previous.defaulted || entry.defaulted;
      duplicateRowCount++;
    } else {
      bySku.set(key, entry);
      entries.push(entry);
      originalCase[key] = sku;
    }
    qtyBySku[key] = bySku.get(key).qty;
  });
  const overflow = entries.find(entry => !Number.isSafeInteger(entry.qty));
  if (overflow) return { error: `The combined quantity for ${overflow.sku} is too large.`, entries: [], invalidRows };
  return {
    qtyBySku, originalCase, entries, invalidRows, combinedSkus, duplicateRowCount,
    totalCount: entries.length + invalidRows.length,
    unitCount: entries.reduce((total, entry) => total + entry.qty, 0),
    rowCount: rows.length,
  };
}

export function parseCsvText(text) {
  const clean = String(text).replace(/^\uFEFF/, "");
  const firstLine = clean.split(/\r?\n/).find(line => line.trim()) || "";
  // Excel in comma-decimal locales saves "CSV" with semicolons.
  const delimiter = firstLine.includes(",") ? "," : firstLine.includes("	") ? "	" : firstLine.includes(";") ? ";" : ",";
  const result = readDelimitedText(clean, delimiter);
  return result.error ? { error: result.error, entries: [], invalidRows: [] } : parseOrderRows(result.rows);
}

export function parsePastedText(text) {
  if (new Blob([text]).size > MAX_CSV_BYTES) return { error: "This list is too large. Paste up to 2 MB at a time.", entries: [], invalidRows: [] };
  return parseOrderRows(String(text).replace(/^\uFEFF/, "").split(/\r\n|\n|\r/).map((line, index) => {
    if (line.includes(",") || line.includes("\t")) {
      const parsed = readDelimitedText(line, line.includes("\t") ? "\t" : ",");
      return { row: index + 1, cells: parsed.error ? [line, "Invalid row"] : parsed.rows[0].cells };
    }
    return { row: index + 1, cells: line.trim().split(/\s+/) };
  }));
}

export async function parseOrderFile(file) {
  const error = validateCsvFile(file);
  if (error) return { error, entries: [], invalidRows: [] };
  if (/\.csv$/i.test(file.name)) return parseCsvText(await file.text());
  try {
    const XLSX = await import("xlsx");
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", cellHTML: false, cellFormula: false });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    if (!sheet) return { error: "This workbook has no worksheet. Add your SKUs to the first sheet.", entries: [], invalidRows: [] };
    const startRow = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]).s.r : 0;
    return parseOrderRows(XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "", blankrows: true }).map((cells, index) => ({ row: startRow + index + 1, cells })));
  } catch (err) {
    return { error: "We couldn’t read this workbook. Save it as .xlsx or CSV and try again.", entries: [], invalidRows: [] };
  }
}

// null/undefined/"" price = not priced for this account. Never treat it as $0.
export function hasPrice(item) {
  return item?.price !== null && item?.price !== undefined && item?.price !== "" && Number.isFinite(Number(item.price));
}

export function getOrderQuantityLimit(item) {
  const max = Number(item.MaxOrderQty);
  const stock = Number(item.stockLevelAbsolute);
  const limits = [Number.isFinite(max) && max > 0 ? Math.floor(max) : Infinity, item.stockLevelAbsolute != null && Number.isFinite(stock) ? Math.max(0, Math.floor(stock)) : Infinity];
  return Math.min(...limits);
}

export function findSkuSuggestion(sku, candidates) {
  const normalize = text => String(text || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const query = normalize(sku);
  if (!query || query.length > 128) return null;
  let best = null, bestScore = 0.65;
  for (const item of candidates) {
    const target = normalize(item.sku);
    if (!target || target.length > 128) continue;
    let distances = Array.from({ length: target.length + 1 }, (_, index) => index);
    for (let i = 1; i <= query.length; i++) {
      const next = [i];
      for (let j = 1; j <= target.length; j++) next[j] = Math.min(next[j - 1] + 1, distances[j] + 1, distances[j - 1] + (query[i - 1] === target[j - 1] ? 0 : 1));
      distances = next;
    }
    const score = 1 - distances[target.length] / Math.max(query.length, target.length);
    if (score > bestScore) { best = item; bestScore = score; }
  }
  return best;
}

// A missing SKU that still exists in search under the exact same code has been discontinued.
export function findDiscontinuedMatch(sku, hits) {
  const normalize = text => String(text || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const query = normalize(sku);
  const hit = query && (hits || []).find(item => normalize(item.sku) === query);
  return hit ? { ...hit, discontinued: true } : null;
}

export function createCsvOrderResolver(getters) {
  function isEntitled(val) {
    const { allow, deny } = getters.getCatalogEntitlement || {};
    const includes = list => list?.variant_ids?.includes(val.id) || list?.product_ids?.includes(val.product_id) || list?.category_ids?.includes(val.category_id);
    return !includes(deny) && (!allow || Boolean(includes(allow)));
  }

  function isAllowed(val) {
    const cart = getters.cart || {};
    const isCanna = getters.getIsCanna;
    const stateCode = getters.getSelectedAddress?.state_code;
    const active = (val.active == null || val.active == 1) && (val.status == null || val.status === "active");
    const productActive = (val.product_active == null || val.product_active == 1) && (val.product_status == null || val.product_status === "active");
    return active && productActive && (cart.price_list_id == "33" || val.tags !== "GGV ONLY") &&
      (!isCanna || val.is_canna == isCanna) && !val.banned_state?.includes(stateCode) && isEntitled(val);
  }

  async function resolveParsedOrder({ parsed, axios, isAlreadyAdded = () => false }) {
    if (parsed.error) return { ok: false, error: parsed.error };
    const { entries, invalidRows = [], combinedSkus = [], duplicateRowCount = 0 } = parsed;
    if (!entries.length && !invalidRows.length) return { ok: false, error: "No products found. Add a SKU and quantity on each row." };
    const skus = entries.map(entry => entry.sku.toUpperCase());
    let items = [];
    if (skus.length) {
      const res = await axios.authApi.post("/get-products-by-skus", { skus });
      if (!res.data?.status || !Array.isArray(res.data.data?.items)) return { ok: false, error: res.data?.error || "We couldn’t match these products. Please try again." };
      items = res.data.data.items;
    }
    const bySku = new Map(items.map(item => [String(item.sku || "").trim().toUpperCase(), item]));
    const resolved = [], addedRows = [], missingRows = [], restrictedRows = [], outOfStockRows = [], duplicateRows = [], cappedRows = [], noPriceRows = [];
    entries.forEach(entry => {
      const item = bySku.get(entry.sku.toUpperCase());
      if (!item) { missingRows.push({ ...entry, type: "missing", reason: "SKU not found" }); return; }
      const row = { ...entry, item, requestedQty: entry.qty };
      if (!isAllowed(item)) { restrictedRows.push({ ...row, reason: "Unavailable for your account or shipping address" }); return; }
      // A missing price means no price is set for this account, not a free product.
      if (!hasPrice(item)) { noPriceRows.push({ ...row, reason: "No price set for your account — contact your rep" }); return; }
      const limit = getOrderQuantityLimit(item);
      if (limit <= 0) { outOfStockRows.push({ ...row, reason: "Out of stock" }); return; }
      if (isAlreadyAdded(item)) { duplicateRows.push({ ...row, reason: "Already in your list or order — skipped" }); return; }
      const qty = Math.min(entry.qty, limit);
      if (qty !== entry.qty) cappedRows.push({ ...row, addedQty: qty, reason: Number(item.MaxOrderQty) === limit ? "Maximum order quantity" : "Available stock" });
      resolved.push({ ...item, qty });
      addedRows.push(row);
    });
    return {
      ok: true, items: resolved,
      summary: emptySummary({
        successCount: resolved.length, totalCount: parsed.totalCount, duplicateRowCount,
        invalidQtySkus: invalidRows.map(row => `${row.sku || '(blank SKU)'} — ${row.reason}`), combinedSkus,
        missingSkus: missingRows.map(row => row.sku), restrictedSkus: restrictedRows.map(row => row.sku),
        outOfStockSkus: outOfStockRows.map(row => row.sku), duplicateSkus: duplicateRows.map(row => row.sku), cappedSkus: cappedRows.map(row => row.sku),
        addedRows, missingRows, restrictedRows, outOfStockRows, duplicateRows, cappedRows, invalidRows,
        noPriceRows, noPriceSkus: noPriceRows.map(row => row.sku),
      }),
    };
  }

  async function resolveCsvFile({ file, ...options }) {
    return resolveParsedOrder({ ...options, parsed: await parseOrderFile(file) });
  }

  async function resolvePastedOrder({ text, ...options }) {
    return resolveParsedOrder({ ...options, parsed: parsePastedText(text) });
  }

  return { resolveCsvFile, resolvePastedOrder, resolveParsedOrder, isAllowed, isEntitled, downloadCsvTemplate };
}

export default function CsvOrderUpload() {
  return createCsvOrderResolver(useStore().getters);
}
